import { named, withDependencies } from '@wix/thunderbolt-ioc'
import {
	BrowserWindow,
	BrowserWindowSymbol,
	EditorFeatureConfigSymbol,
	ViewerManagerStructureAPISymbol,
} from '@wix/thunderbolt-symbols'
import type { ViewerAPI } from 'thunderbolt-viewer-manager-types'
import { name } from '../symbols'
import type { ILayoutUtils, ViewerManagerLayoutApiEditorConfig } from '../types'
import { GroupMeasuresSymbol, IGroupMeasures } from 'ds-feature-components-measures'
import { MeasuresApiSymbol } from 'ds-feature-measures-api'
import type { IMeasuresApi } from '@wix/thunderbolt-becky-types'

// shell comps that are not part of the full structure
const IGNORED_COMPS = ['SCROLL_TO_TOP', 'SCROLL_TO_BOTTOM', 'FONTS']

export const LayoutUtils = withDependencies(
	[
		named(EditorFeatureConfigSymbol, name),
		BrowserWindowSymbol,
		ViewerManagerStructureAPISymbol,
		GroupMeasuresSymbol,
		MeasuresApiSymbol,
	],
	(
		config: ViewerManagerLayoutApiEditorConfig,
		window: NonNullable<BrowserWindow>,
		structureAPI: ViewerAPI['structure'],
		{ isGroupChild }: IGroupMeasures,
		measuresApi: IMeasuresApi
	): ILayoutUtils => {
		const isStructuralComp = (compId: string) =>
			!IGNORED_COMPS.includes(compId) && !!structureAPI.getStructure(compId)

		const getStructureParent = (element: HTMLElement): HTMLElement | null => {
			const parentElement = element.parentElement
			if (!parentElement) {
				return null
			}
			if (structureAPI.getStructure(parentElement.id)) {
				return parentElement
			}
			return getStructureParent(parentElement)
		}

		const isDisplayNoneRec = (element: HTMLElement | null): boolean => {
			if (!element) {
				return false
			}
			if (getComputedStyle(element).display === 'none') {
				return true
			}
			return isDisplayNoneRec(getStructureParent(element))
		}

		const isDisplayed = (compId: string) => {
			const element = measuresApi.getCompElement(compId)
			return !!element && !isDisplayNoneRec(element)
		}

		const getParentGroupIds = (compIds: Set<string>) => {
			const groupComps = new Set<string>()
			compIds.forEach((compId) => {
				if (isGroupChild(compId)) {
					const parentId = structureAPI.getStructure(compId).parent!
					groupComps.add(parentId)
				}
			})
			return groupComps
		}

		const getCompIdsWithSiblings = (compIds: Set<string>) => {
			const compIdsWithSiblings = new Set<string>()
			compIds.forEach((compId) => {
				if (!compIdsWithSiblings.has(compId)) {
					const parentId = structureAPI.getStructure(compId).parent
					const parent = parentId && structureAPI.getStructure(parentId)
					if (parent) {
						const siblings = structureAPI.getStructure(parentId).components || [compId]
						siblings.forEach((siblingId) => {
							if (isStructuralComp(siblingId)) {
								compIdsWithSiblings.add(siblingId)
							}
						})
					} else {
						compIdsWithSiblings.add(compId)
					}
				}
			})
			return compIdsWithSiblings
		}

		const getRenderedComps = (compIdsArr: Array<string>) => {
			const compIds = new Set(compIdsArr.filter(isStructuralComp))
			compIds.delete('SITE_STYLES')

			// We need to trigger layout done for group component on child layout change in order to fix group's measures
			const groupCompIds = getParentGroupIds(compIds)

			// there is no way to observe if siblings comps where affected by comp change. for example, pushed down in resize and push
			// therefore, always add siblings components to rendered comps as well (only components that are part of the full structure)
			return getCompIdsWithSiblings(new Set([...groupCompIds, ...compIds]))
		}

		return {
			isStructuralComp,
			getRenderedComps,
			isDisplayed,
		}
	}
)
